import React, { useEffect, useMemo, useState } from 'react';
import { getAlerts, Event, ListResult } from '../../data/eventsService';
import AlertCard from './AlertCard';
import EventDetailModal from '../events/EventDetailModal';

type AlertType = 'all' | 'session_no_show' | 'seeker_rejected_match' | 'guide_rejected_match' | 'seeker_not_matched' | 'rematch_limit_reached';

export default function AlertPlaceholderPage() {
  const [alertType, setAlertType] = useState<AlertType>('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [res, setRes] = useState<ListResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const totalPages = useMemo(() => {
    if (!res) return 1;
    return Math.max(1, Math.ceil(res.total / pageSize));
  }, [res, pageSize]);

  useEffect(() => {
    setLoading(true);
    getAlerts({
      event_type: alertType,
      page,
      pageSize,
    })
      .then(setRes)
      .finally(() => setLoading(false));
  }, [alertType, page, pageSize]);

  const alertTypeConfig = [
    { value: 'all', label: 'All Alerts' },
    { value: 'session_no_show', label: 'No-Shows' },
    { value: 'seeker_rejected_match', label: 'Seeker Rejects' },
    { value: 'guide_rejected_match', label: 'Guide Rejects' },
    { value: 'seeker_not_matched', label: 'No Matches' },
    { value: 'rematch_limit_reached', label: 'Rejection Limits' },
  ];

  const getAlertStats = () => {
    if (!res?.items) return { noShow: 0, rejections: 0, noMatch: 0, rejectionLimit: 0 };

    // Get counts from current page (for display purposes)
    const stats = {
      noShow: res.items.filter((e) => e.event_name === 'session_no_show').length,
      rejections:
        res.items.filter((e) => e.event_name === 'seeker_rejected_match').length +
        res.items.filter((e) => e.event_name === 'guide_rejected_match').length,
      noMatch: res.items.filter((e) => e.event_name === 'seeker_not_matched').length,
      rejectionLimit: res.items.filter((e) => e.event_name === 'rematch_limit_reached').length,
    };

    return stats;
  };

  const stats = getAlertStats();

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">Alerts Center</h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Monitor critical events and user issues across the platform
          </p>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">Total Alerts</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {res?.total ?? 0}
          </div>
        </div>
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">No-Shows</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {stats.noShow}
          </div>
        </div>
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">Rejections</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {stats.rejections}
          </div>
        </div>
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">No Matches</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {stats.noMatch}
          </div>
        </div>
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">Rejection Limits</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {stats.rejectionLimit}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="rounded-xl border border-[#2C2731] bg-[#2C2731] p-2 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {alertTypeConfig.map((type) => (
            <button
              key={type.value}
              onClick={() => {
                setPage(1);
                setAlertType(type.value as AlertType);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                alertType === type.value
                  ? 'bg-[#FFFCEF] text-[#2C2731]'
                  : 'bg-transparent text-[#F2EAC3] hover:text-[#FEF6D6] hover:bg-[#214D3C]'
              }`}
            >
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {loading && (
          <div className="text-center py-12 text-[#2C2731]/70">
            <div className="text-lg">Loading alerts...</div>
          </div>
        )}

        {!loading && (!res?.items || res.items.length === 0) && (
          <div className="text-center py-16 rounded-xl border border-[#2C2731]/20 bg-white shadow-sm">
            <div className="text-lg font-medium text-[#2C2731] mb-2 font-recoleta">
              No alerts found
            </div>
            <div className="text-sm text-[#2C2731]/70">
              {alertType === 'all'
                ? 'No alerts to display. Everything looks good!'
                : `No ${alertType} alerts at this time.`}
            </div>
          </div>
        )}

        {!loading &&
          res?.items &&
          res.items.map((event) => (
            <AlertCard key={event.id} event={event} onClick={() => setSelectedEvent(event)} />
          ))}
      </div>

      {/* Pagination */}
      {!loading && res && res.items.length > 0 && (
        <div className="flex flex-col gap-3 text-sm text-[#2C2731]/80 md:flex-row md:items-center md:justify-between">
          <div>
            Showing {res.items.length} of {res.total} alerts
          </div>
          <div className="flex items-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
                page <= 1
                  ? 'cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40'
                  : 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]'
              }`}
            >
              Previous
            </button>
            <div className="px-2 text-sm text-[#2C2731]">
              Page {page} / {totalPages}
            </div>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
                page >= totalPages
                  ? 'cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40'
                  : 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}


